import { useEffect, useMemo, useRef, useState } from 'react';
import { ChevronLeft, ChevronRight, Loader2 } from 'lucide-react';
import {
  BRANDS,
  BRANDS_BATCH2,
  BRANDS_BATCH3,
  BRAND_CUES,
  brandImageUrl,
  createBrandItem,
  deleteBrandItem,
  adoptBrandProposal,
  replaceBrandItems,
  deleteBrandSample,
  listBrandSample,
  markBrandSample,
  updateBrandItem,
  updateBrandName,
  type BrandItem,
  type BrandProposal,
  type BrandSample,
  type DatasetSplit,
} from '../../api/brandLocate';

const COLORS = ['#00e0c8', '#ff4d1a', '#3b8bff', '#d946ef', '#ffe14a', '#ff3d8a', '#39d4ff', '#b6ff3b'];
const DRAFT_COLOR = '#ff4d1a';
const MODEL_COLOR: Record<string, string> = {
  'qwen3.8-flash': '#2d6a4f',
  'qwen3.7-plus': '#2d6a4f',
  'gemini-3.8-flash': '#9a3412',
  'seed-lite': '#1d4e89',
};

type Drag =
  | { kind: 'move'; itemId: number; dx: number; dy: number; origin: number[] }
  | { kind: 'resize'; itemId: number; edge: string; origin: number[] };

type Arm = {
  kind: 'move' | 'resize';
  itemId: number;
  edge: string;
  dx: number;
  dy: number;
  origin: number[];
  sx: number;
  sy: number;
};

const BATCH3_LIST = new Set<string>(BRANDS_BATCH3);
const OUTSIDE_BRAND = '名单外';

function inBrandList(brand: string) {
  return BATCH3_LIST.has(brand);
}

function hasBox(item: { box?: number[] | null }) {
  return Array.isArray(item.box) && item.box.length === 4;
}

function boxKey(item: { cue: string; box: number[]; reason?: string }) {
  if (!hasBox(item)) return `reason:${item.reason || ''}`;
  return `${item.cue}:${item.box.map((value) => value.toFixed(4)).join(',')}`;
}

function rememberedModel(sample: BrandSample) {
  if (sample.picked_model) return sample.picked_model;
  const saved = sample.items.map(boxKey).sort().join('|');
  const hit = sample.proposals.find((proposal) => proposal.brand === sample.brand && proposal.items.map(boxKey).sort().join('|') === saved);
  return hit?.model ?? '';
}

export function BrandPage() {
  const [batch, setBatch] = useState<1 | 2 | 3>(() => {
    const saved = sessionStorage.getItem('brand-batch');
    if (saved === '1') return 1;
    if (saved === '3') return 3;
    return 2;
  });
  const [split, setSplit] = useState<DatasetSplit>(() => (sessionStorage.getItem('brand-split') === 'test' ? 'test' : 'train'));
  const [offset, setOffset] = useState(0);
  const [reload, setReload] = useState(0);
  const [landOnOpen, setLandOnOpen] = useState(() => sessionStorage.getItem('brand-batch') !== '1');
  const [openOffset, setOpenOffset] = useState(-1);
  const [openTotal, setOpenTotal] = useState(0);
  const [allTotal, setAllTotal] = useState(0);
  const [trainTotal, setTrainTotal] = useState(0);
  const [testTotal, setTestTotal] = useState(0);
  const [total, setTotal] = useState(0);
  const [sample, setSample] = useState<BrandSample | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);
  const [drawMode, setDrawMode] = useState(false);
  const [draft, setDraft] = useState<{ box: number[]; cue: string } | null>(null);
  const [pageText, setPageText] = useState('1');
  const [pickedModel, setPickedModel] = useState('');
  const baselines = useRef(new Map<number, { brand: string; items: BrandItem[] }>());

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const page = await listBrandSample(offset, landOnOpen, batch, split);
        if (cancelled) return;
        setOpenOffset(page.open_offset);
        setOpenTotal(page.open_total);
        setAllTotal(page.all_total || 0);
        setTrainTotal(page.train_total || 0);
        setTestTotal(page.test_total || 0);
        if (landOnOpen) setLandOnOpen(false);
        if (page.offset !== offset) {
          setOffset(page.offset);
          return;
        }
        if (!page.sample && offset > 0) {
          setOffset(Math.max(0, Math.min(offset - 1, page.total - 1)));
          return;
        }
        setTotal(page.total);
        setSample(page.sample);
        if (page.sample && !baselines.current.has(page.sample.id)) {
          baselines.current.set(page.sample.id, {
            brand: page.sample.brand,
            items: page.sample.items.map((item) => ({ ...item, box: Array.isArray(item.box) ? [...item.box] : [] })),
          });
        }
        setPickedModel(page.sample ? rememberedModel(page.sample) : '');
        setSelectedId(page.sample?.items.find((item) => hasBox(item))?.id ?? null);
        setDraft(null);
        setDrawMode(false);
        setMsg(null);
      } catch (e) {
        if (!cancelled) setError(e instanceof Error ? e.message : '加载失败');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [offset, reload, landOnOpen, batch, split]);

  useEffect(() => {
    setPageText(total ? String(offset + 1) : '');
  }, [offset, total]);

  const current = sample?.items.find((item) => item.id === selectedId && hasBox(item)) ?? null;
  const notes = (sample?.items ?? []).filter((item) => !hasBox(item) && item.reason);
  const sampleRef = useRef(sample);
  const currentRef = useRef(current);
  const draftRef = useRef(draft);
  const savingRef = useRef(saving);
  const offsetRef = useRef(offset);
  const totalRef = useRef(total);
  const reviewedIds = useRef(new Set<number>());
  const reviewFlight = useRef(new Map<number, Promise<void>>());
  const removeRef = useRef<() => void>(() => {});
  const deletePageRef = useRef<() => void>(() => {});
  offsetRef.current = offset;
  totalRef.current = total;
  sampleRef.current = sample;
  currentRef.current = current;
  draftRef.current = draft;
  savingRef.current = saving;

  const reviewRef = useRef<() => Promise<void>>(async () => {});
  reviewRef.current = async () => {
    const page = sampleRef.current;
    if (!page || page.qc_status === 'ok') return;
    const pending = reviewFlight.current.get(page.id);
    if (pending) return pending;
    const job = (async () => {
      if (reviewedIds.current.has(page.id)) return;
      reviewedIds.current.add(page.id);
      sampleRef.current = { ...page, qc_status: 'ok' };
      setSample((prev) => (prev && prev.id === page.id ? { ...prev, qc_status: 'ok' } : prev));
      setOpenTotal((count) => Math.max(0, count - 1));
      try {
        await markBrandSample(page.id);
      } catch (e) {
        reviewedIds.current.delete(page.id);
        setMsg(e instanceof Error ? e.message : '质检标记失败');
        setReload((value) => value + 1);
      } finally {
        reviewFlight.current.delete(page.id);
      }
    })();
    reviewFlight.current.set(page.id, job);
    return job;
  };

  const leaveTo = (next: number) => {
    if (next === offsetRef.current) return;
    if (next > offsetRef.current) void reviewRef.current();
    setOffset(next);
  };

  const chooseSplit = (next: DatasetSplit) => {
    if (split === next) return;
    sessionStorage.setItem('brand-split', next);
    setSplit(next);
    setOffset(0);
    setLandOnOpen(true);
  };

  const jumpToPage = () => {
    const page = Number(pageText.trim());
    if (!Number.isInteger(page) || page < 1 || page > total) {
      setPageText(total ? String(offset + 1) : '');
      return;
    }
    leaveTo(page - 1);
  };

  const patchItem = (itemId: number, next: Partial<BrandItem>) => {
    setSample((prev) => {
      if (!prev) return prev;
      const items = prev.items.map((item) => (item.id === itemId ? { ...item, ...next } : item));
      const updated = { ...prev, items };
      sampleRef.current = updated;
      return updated;
    });
  };

  const saveItem = async (item: BrandItem) => {
    if (savingRef.current) return;
    savingRef.current = true;
    setSaving(true);
    setMsg(null);
    try {
      const saved = await updateBrandItem(item.id, { cue: item.cue, reason: item.reason, box: item.box });
      patchItem(item.id, saved);
      await reviewRef.current();
    } catch (e) {
      setMsg(e instanceof Error ? e.message : '保存失败');
      setReload((value) => value + 1);
    } finally {
      savingRef.current = false;
      setSaving(false);
    }
  };

  const saveDraft = async (cue: string) => {
    const pending = draftRef.current;
    const page = sampleRef.current;
    if (!pending || !page || savingRef.current) return;
    savingRef.current = true;
    setSaving(true);
    setMsg(null);
    try {
      const saved = await createBrandItem(page.id, { cue, box: pending.box });
      setSample((prev) => {
        if (!prev || prev.id !== page.id) return prev;
        const updated = { ...prev, items: [...prev.items, saved] };
        sampleRef.current = updated;
        return updated;
      });
      setSelectedId(saved.id);
      setDraft(null);
      setMsg('已保存');
      await reviewRef.current();
    } catch (e) {
      setMsg(e instanceof Error ? e.message : '保存失败');
    } finally {
      savingRef.current = false;
      setSaving(false);
    }
  };

  const changeBrand = async (brand: string) => {
    const page = sampleRef.current;
    if (!page || savingRef.current) return;
    setSample((prev) => (prev && prev.id === page.id ? { ...prev, brand } : prev));
    sampleRef.current = { ...page, brand };
    savingRef.current = true;
    setSaving(true);
    setMsg(null);
    try {
      await updateBrandName(page.id, brand);
      setSample((prev) => (prev && prev.id === page.id ? { ...prev, brand, qc_status: 'ok' } : prev));
      reviewedIds.current.add(page.id);
      if (page.qc_status !== 'ok') setOpenTotal((count) => Math.max(0, count - 1));
      setMsg('品牌已改');
    } catch (e) {
      setMsg(e instanceof Error ? e.message : '品牌保存失败');
      setReload((value) => value + 1);
    } finally {
      savingRef.current = false;
      setSaving(false);
    }
  };

  const removeBox = () => {
    const item = currentRef.current;
    const page = sampleRef.current;
    if (!item || !page || savingRef.current) return;
    setSaving(true);
    void deleteBrandItem(item.id).then(() => {
      setSample((prev) => {
        if (!prev || prev.id !== page.id) return prev;
        const items = prev.items.filter((entry) => entry.id !== item.id);
        const updated = { ...prev, items, qc_status: 'ok' };
        sampleRef.current = updated;
        return updated;
      });
      setSelectedId(null);
      reviewedIds.current.add(page.id);
      if (page.qc_status !== 'ok') setOpenTotal((count) => Math.max(0, count - 1));
      setMsg('已删除这个框');
    }).catch((e) => {
      setMsg(e instanceof Error ? e.message : '删除失败');
    }).finally(() => setSaving(false));
  };
  removeRef.current = removeBox;

  const deletePage = () => {
    const page = sampleRef.current;
    if (!page || savingRef.current) return;
    setSaving(true);
    void deleteBrandSample(page.id).then(() => {
      setMsg('已删除这张');
      setReload((value) => value + 1);
    }).catch((e) => {
      setMsg(e instanceof Error ? e.message : '删除失败');
    }).finally(() => setSaving(false));
  };
  deletePageRef.current = deletePage;

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      const target = event.target instanceof HTMLElement ? event.target : null;
      const typing = !!target && (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.tagName === 'SELECT' || target.isContentEditable);
      if (typing || event.metaKey || event.ctrlKey || event.altKey) return;
      if (event.key === 'Backspace' || event.key === 'Delete') {
        event.preventDefault();
        removeRef.current();
        return;
      }
      if (event.key === 'ArrowRight') {
        event.preventDefault();
        const next = offsetRef.current + 1;
        if (next < totalRef.current) leaveTo(next);
        else void reviewRef.current();
      } else if (event.key === 'ArrowLeft') {
        event.preventDefault();
        const next = offsetRef.current - 1;
        if (next >= 0) leaveTo(next);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [total]);

  const adopt = async (model: string) => {
    const page = sampleRef.current;
    if (!page || savingRef.current) return;
    savingRef.current = true;
    setSaving(true);
    setMsg(null);
    try {
      const hadCollar = page.items.some((item) => item.cue === '领标' && hasBox(item));
      const saved = await adoptBrandProposal(page.id, model);
      const updated = { ...page, brand: saved.brand, items: saved.items, qc_status: 'ok', qc_by: 'human', picked_model: model };
      sampleRef.current = updated;
      setSample(updated);
      setPickedModel(model);
      setSelectedId(saved.items.find((item) => hasBox(item))?.id ?? null);
      reviewedIds.current.add(page.id);
      if (page.qc_status !== 'ok') setOpenTotal((count) => Math.max(0, count - 1));
      setMsg(hadCollar ? '已并上这个模型的框，领标还留着' : '已用这个模型的框。可以换另一个，或撤销重选');
    } catch (e) {
      setMsg(e instanceof Error ? e.message : '套用失败');
    } finally {
      savingRef.current = false;
      setSaving(false);
    }
  };

  const undoPick = async () => {
    const page = sampleRef.current;
    const baseline = page ? baselines.current.get(page.id) : undefined;
    if (!page || !baseline || savingRef.current) return;
    savingRef.current = true;
    setSaving(true);
    setMsg(null);
    try {
      const saved = await replaceBrandItems(page.id, {
        brand: baseline.brand,
        items: baseline.items.map((item) => ({ cue: item.cue, reason: item.reason, box: item.box })),
      });
      const updated = { ...page, brand: saved.brand, items: saved.items, qc_status: 'ok', qc_by: 'human', picked_model: '' };
      sampleRef.current = updated;
      setSample(updated);
      setPickedModel('');
      setSelectedId(saved.items.find((item) => hasBox(item))?.id ?? null);
      setMsg('已撤销，可以重选');
    } catch (e) {
      setMsg(e instanceof Error ? e.message : '撤销失败');
    } finally {
      savingRef.current = false;
      setSaving(false);
    }
  };

  const proposals = sample?.proposals ?? [];
  const statusLabel = sample?.qc_by === 'model' ? '模型已质检' : sample?.qc_status === 'ok' ? '质检通过' : '未标注';
  const statusTone = sample?.qc_status === 'ok' ? 'bg-[#e7f8ee] text-[#0d7a3a]' : 'bg-[#fff6dc] text-[#8a5a00]';
  const baseBrand = !sample ? '' : sample.source_label === '均不是' ? '没有品牌' : sample.source_label;

  return (
    <div className="h-full grid grid-cols-[minmax(0,1fr)_320px]">
      <div className="min-w-0 flex flex-col">
        <div className="shrink-0 border-b border-[var(--line)] bg-[var(--paper)]">
          <div className="flex items-center gap-2 px-3 pt-2 overflow-x-auto">
            <div className="flex items-center shrink-0 rounded-[var(--radius-sm)] border border-[var(--line)] bg-[var(--bg)]">
              <button
                className={`btn-ghost shrink-0 whitespace-nowrap rounded-r-none px-2 text-sm ${batch === 1 ? 'bg-[var(--ink)] text-[var(--paper)]' : ''}`}
                type="button"
                onClick={() => {
                  if (batch === 1) return;
                  sessionStorage.setItem('brand-batch', '1');
                  setBatch(1);
                  setOffset(0);
                  setLandOnOpen(false);
                }}
              >
                第一批
              </button>
              <button
                className={`btn-ghost shrink-0 whitespace-nowrap rounded-none px-2 text-sm ${batch === 2 ? 'bg-[var(--ink)] text-[var(--paper)]' : ''}`}
                type="button"
                onClick={() => {
                  if (batch === 2) return;
                  sessionStorage.setItem('brand-batch', '2');
                  setBatch(2);
                  setOffset(0);
                  setLandOnOpen(true);
                }}
              >
                第二批
              </button>
              <button
                className={`btn-ghost shrink-0 whitespace-nowrap rounded-l-none px-2 text-sm ${batch === 3 ? 'bg-[var(--ink)] text-[var(--paper)]' : ''}`}
                type="button"
                onClick={() => {
                  if (batch === 3) return;
                  sessionStorage.setItem('brand-batch', '3');
                  setBatch(3);
                  setOffset(0);
                  setLandOnOpen(true);
                }}
              >
                第三批
              </button>
            </div>
            <div className="flex items-center shrink-0 rounded-[var(--radius-sm)] border border-[var(--line)] bg-[var(--bg)]">
              <button
                className={`btn-ghost shrink-0 whitespace-nowrap rounded-r-none px-2 text-sm ${split === 'train' ? 'bg-[var(--ink)] text-[var(--paper)]' : ''}`}
                type="button"
                onClick={() => chooseSplit('train')}
              >
                训练集{trainTotal ? ` ${trainTotal}` : ''}
              </button>
              <button
                className={`btn-ghost shrink-0 whitespace-nowrap rounded-l-none px-2 text-sm ${split === 'test' ? 'bg-[var(--ink)] text-[var(--paper)]' : ''}`}
                type="button"
                onClick={() => chooseSplit('test')}
              >
                测试集{testTotal ? ` ${testTotal}` : ''}
              </button>
            </div>
            <div className="flex items-center shrink-0 rounded-[var(--radius-sm)] border border-[var(--line)] bg-[var(--bg)]">
              <button className="btn-ghost shrink-0 whitespace-nowrap rounded-r-none px-2" disabled={offset <= 0} onClick={() => leaveTo(offset - 1)} aria-label="上一张">
                <ChevronLeft className="w-4 h-4" />
              </button>
              <form
                className="flex items-center gap-1 px-1 text-sm text-[var(--muted)] border-x border-[var(--line)]"
                onSubmit={(event) => {
                  event.preventDefault();
                  jumpToPage();
                }}
              >
                <input
                  aria-label="页码"
                  inputMode="numeric"
                  className="w-14 bg-transparent py-1 text-right text-sm tabular-nums text-[var(--ink)] outline-none"
                  value={pageText}
                  onChange={(event) => setPageText(event.target.value.replace(/[^\d]/g, ''))}
                  onBlur={jumpToPage}
                />
                <span className="tabular-nums pr-1">/ {total || 0}</span>
              </form>
              <button className="btn-ghost shrink-0 whitespace-nowrap rounded-l-none px-2" disabled={offset + 1 >= total} onClick={() => leaveTo(offset + 1)} aria-label="下一张">
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
            <button
              className="btn-ghost shrink-0 whitespace-nowrap text-sm"
              type="button"
              disabled={openTotal === 0 || offset === openOffset}
              onClick={() => setLandOnOpen(true)}
            >
              {openTotal === 0 ? '已标完' : `未标注 ${openTotal}`}
            </button>
            <span className="shrink-0 text-xs text-[var(--muted)] whitespace-nowrap">{batch === 1 ? '第一批' : batch === 2 ? '第二批' : '第三批'} · {split === 'test' ? '测试集' : '训练集'} {allTotal || total}</span>
            <div className="flex-1" />
            <button
              className={`btn-ghost shrink-0 whitespace-nowrap text-sm ${drawMode ? 'bg-[var(--accent-soft)] text-[var(--ink)]' : ''}`}
              onClick={() => {
                setDrawMode((value) => !value);
                setDraft(null);
                setMsg(null);
              }}
            >
              {drawMode ? '取消画框' : '新建框'}
            </button>
            <button className="btn-ghost shrink-0 whitespace-nowrap text-sm text-[#9a3412]" type="button" disabled={!sample || saving} onClick={() => deletePageRef.current()}>
              删除这张
            </button>
          </div>
          <div className="flex items-center gap-2 px-3 pt-1.5 pb-2 min-w-0">
            {sample && <span className={`shrink-0 whitespace-nowrap text-xs px-2 py-0.5 rounded-full ${statusTone}`}>{statusLabel}</span>}
            <span className="min-w-0 truncate text-xs text-[var(--muted)]">{sample ? `${sample.split === 'batch2-test' || sample.split === 'test' ? '测试集' : '训练集'} · ${sample.sample_key}` : ''}</span>
            {baseBrand && <span className="shrink-0 text-xs text-[var(--ink)]">原品牌 {baseBrand}</span>}
            <span className="ml-auto shrink-0 flex items-center gap-2 text-xs text-[var(--muted)] whitespace-nowrap">
              {saving && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
              {msg && <span className="text-[var(--ink)]">{msg}</span>}
              <span>右键确认并下一张</span>
            </span>
          </div>
        </div>
        <div className="flex-1 overflow-auto p-4 bg-[var(--bg)]">
          {loading && <div className="text-sm text-[var(--muted)]">正在读取…</div>}
          {error && <div className="text-sm text-[var(--red)]">{error}</div>}
          {sample && notes.length > 0 && (
            <div className="mb-3 rounded-[var(--radius)] border border-[var(--line)] bg-[var(--paper)] px-3 py-2">
              <div className="text-xs text-[var(--muted)]">整体原因，不画框</div>
              {notes.map((item) => (
                <p key={item.id} className="mt-1 text-sm leading-relaxed text-[var(--ink)]">{item.reason}</p>
              ))}
            </div>
          )}
          {sample && (
            <BrandCanvas
              key={sample.id}
              sampleId={sample.id}
              items={sample.items}
              proposals={proposals}
              selectedId={selectedId}
              onSelect={setSelectedId}
              onPreview={(itemId, box) => patchItem(itemId, { box })}
              onCommit={(itemId, box) => {
                const item = sampleRef.current?.items.find((entry) => entry.id === itemId);
                if (!item) return;
                const next = { ...item, box };
                patchItem(itemId, { box });
                void saveItem(next);
              }}
              drawMode={drawMode}
              draftBox={draft?.box ?? null}
              onDraw={(box) => {
                const next = { box, cue: 'logo' };
                draftRef.current = next;
                setDraft(next);
                setDrawMode(false);
                setSelectedId(null);
                void saveDraft('logo');
              }}
            />
          )}
        </div>
      </div>
      <aside className="border-l border-[var(--line)] bg-[var(--paper)] overflow-auto p-4">
        <h2 className="text-sm font-semibold mb-3">品牌特征定位</h2>
        {baseBrand && (
          <div className="mb-4 rounded-[var(--radius-sm)] border border-[var(--line)] bg-[var(--bg)] px-3 py-2">
            <div className="text-xs text-[var(--muted)]">原品牌</div>
            <div className="mt-1 text-sm font-semibold text-[var(--ink)]">{baseBrand}</div>
          </div>
        )}
        {proposals.length > 0 && (
          <div className="space-y-2 mb-4">
            <p className="text-xs text-[var(--muted)]">虚线是三个模型的框。用这个会把模型的框并进来，已经框好的领标会留下。</p>
            {proposals.map((proposal) => (
              <div key={proposal.model} className={`border rounded-[var(--radius-sm)] p-2 ${pickedModel === proposal.model ? 'border-[var(--ink)]' : 'border-[var(--line)]'}`}>
                <div className="text-xs font-semibold" style={{ color: MODEL_COLOR[proposal.model] || '#1c1914' }}>{proposal.label}</div>
                <div className="text-xs text-[var(--ink)] mt-1">
                  {proposal.brand || '（空品牌）'}
                  {batch === 3 && proposal.brand && !inBrandList(proposal.brand) && (
                    <span className="ml-1 rounded-full bg-[#fff6dc] px-1.5 py-0.5 text-[10px] text-[#8a5a00]">名单外</span>
                  )}
                  <span className="text-[var(--muted)]"> · {proposal.name || '（空名称）'}</span>
                </div>
                <div className="mt-1 space-y-1">
                  {proposal.items.length === 0 && <div className="text-[10px] text-[var(--muted)]">没有框</div>}
                  {proposal.items.length === 0 && proposal.reason && <div className="text-xs leading-relaxed text-[var(--ink)]">{proposal.reason}</div>}
                  {proposal.items.map((item, index) => (
                    <div key={`${proposal.model}-${index}`}>
                      <div className="text-[10px] text-[var(--muted)]">{item.cue || '整体'}</div>
                      {item.reason && <div className="text-xs leading-relaxed text-[var(--ink)]">{item.reason}</div>}
                    </div>
                  ))}
                </div>
                <button className="btn-ghost text-xs mt-1" type="button" disabled={saving || pickedModel === proposal.model} onClick={() => void adopt(proposal.model)}>
                  {pickedModel === proposal.model ? '正在用' : '用这个'}
                </button>
              </div>
            ))}
            {pickedModel && (
              <button className="btn-ghost text-xs" type="button" disabled={saving} onClick={() => void undoPick()}>
                撤销重选
              </button>
            )}
          </div>
        )}
        {notes.map((item) => (
          <label key={item.id} className="block text-xs text-[var(--muted)] mb-4">
            整体原因，不画框
            <textarea
              className="mt-1 w-full min-h-20 border border-[var(--line)] rounded-[var(--radius-sm)] bg-[var(--paper)] px-2 py-1.5 text-sm leading-relaxed text-[var(--ink)]"
              value={item.reason}
              onChange={(event) => patchItem(item.id, { reason: event.target.value })}
              onBlur={() => {
                const latest = sampleRef.current?.items.find((entry) => entry.id === item.id);
                if (latest) void saveItem(latest);
              }}
            />
          </label>
        ))}
        {sample && (
          <label className="block text-xs text-[var(--muted)] mb-4">
            当前品牌
            {batch === 3 && !inBrandList(sample.brand) && (
              <span className="ml-1 rounded-full bg-[#fff6dc] px-1.5 py-0.5 text-[10px] text-[#8a5a00]">名单外</span>
            )}
            <select
              className="mt-1 w-full border border-[var(--line)] rounded-[var(--radius-sm)] bg-[var(--paper)] px-2 py-1.5 text-sm text-[var(--ink)]"
              value={batch === 3 && !inBrandList(sample.brand) ? OUTSIDE_BRAND : sample.brand}
              onChange={(event) => {
                if (event.target.value === OUTSIDE_BRAND) return;
                void changeBrand(event.target.value);
              }}
            >
              {batch === 3 && !inBrandList(sample.brand) && <option value={OUTSIDE_BRAND}>{OUTSIDE_BRAND}</option>}
              {(batch === 3 ? BRANDS_BATCH3 : batch === 2 ? BRANDS_BATCH2 : BRANDS).map((brand) => (
                <option key={brand} value={brand}>{brand}</option>
              ))}
            </select>
          </label>
        )}
        {sample && batch === 3 && (
          <form
            className="mb-4"
            onSubmit={(event) => {
              event.preventDefault();
              const input = event.currentTarget.elements.namedItem('other-brand');
              const value = input instanceof HTMLInputElement ? input.value.trim() : '';
              if (!value || value === sample.brand) return;
              void changeBrand(value);
            }}
          >
            <label className="block text-xs text-[var(--muted)]">
              名单外的品牌
              <input
                key={`${sample.id}:${inBrandList(sample.brand) ? '' : sample.brand}`}
                name="other-brand"
                defaultValue={inBrandList(sample.brand) ? '' : sample.brand}
                className="mt-1 w-full border border-[var(--line)] rounded-[var(--radius-sm)] bg-[var(--paper)] px-2 py-1.5 text-sm text-[var(--ink)]"
                placeholder="名单里没有时，写这个牌子的名字"
              />
            </label>
            <button className="btn-ghost text-xs mt-1" type="submit">用这个名字</button>
          </form>
        )}
        {drawMode && <p className="text-sm text-[var(--muted)] mb-3">在图上按住拖出一个新框。松开后先记成 logo，可以再改类型。</p>}
        {current && (
          <div className="space-y-3">
            <label className="block text-xs text-[var(--muted)]">
              标识
              <select
                className="mt-1 w-full border border-[var(--line)] rounded-[var(--radius-sm)] bg-[var(--paper)] px-2 py-1.5 text-sm text-[var(--ink)]"
                value={current.cue}
                onChange={(event) => {
                  const cue = event.target.value;
                  const next = { ...current, cue };
                  patchItem(current.id, { cue });
                  void saveItem(next);
                }}
              >
                {BRAND_CUES.map((cue) => (
                  <option key={cue} value={cue}>{cue}</option>
                ))}
              </select>
            </label>
            <p className="text-xs text-[var(--muted)] leading-relaxed">{current.reason || '拖动或拉边都会自动保存'}</p>
            <button className="btn-ghost text-sm text-[#9a3412]" type="button" disabled={saving} onClick={() => removeRef.current()}>
              删除这个框
            </button>
          </div>
        )}
        {!current && !drawMode && <p className="text-sm text-[var(--muted)]">点「新建框」补一个外部标识。没有单独标识、只能从整体认出时，可以只留 reason，写明是哪种经典包型或图案。</p>}
        <ul className="mt-6 space-y-2">
          {sample?.items.filter(hasBox).map((item, index) => (
            <li key={item.id}>
              <button
                type="button"
                className={`w-full text-left text-xs px-2 py-1.5 rounded-[var(--radius-sm)] border ${selectedId === item.id ? 'border-[var(--ink)]' : 'border-[var(--line)]'}`}
                onClick={() => setSelectedId(item.id)}
              >
                <span className="inline-block w-2 h-2 rounded-full mr-2" style={{ background: COLORS[index % COLORS.length] }} />
                {item.cue}
              </button>
            </li>
          ))}
        </ul>
      </aside>
    </div>
  );
}

function BrandCanvas({
  sampleId,
  items,
  proposals,
  selectedId,
  onSelect,
  onPreview,
  onCommit,
  drawMode,
  draftBox,
  onDraw,
}: {
  sampleId: number;
  items: BrandItem[];
  proposals: BrandProposal[];
  selectedId: number | null;
  onSelect: (id: number) => void;
  onPreview: (itemId: number, box: number[]) => void;
  onCommit: (itemId: number, box: number[]) => void;
  drawMode: boolean;
  draftBox: number[] | null;
  onDraw: (box: number[]) => void;
}) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const dragRef = useRef<Drag | null>(null);
  const armRef = useRef<Arm | null>(null);
  const drawRef = useRef<{ x: number; y: number; sx: number; sy: number } | null>(null);
  const drawModeRef = useRef(drawMode);
  const itemsRef = useRef(items);
  const selectedRef = useRef(selectedId);
  drawModeRef.current = drawMode;
  itemsRef.current = items;
  selectedRef.current = selectedId;
  const [frame, setFrame] = useState<{ id: number; image: HTMLImageElement } | null>(null);
  const [imageError, setImageError] = useState<string | null>(null);
  const image = frame?.id === sampleId ? frame.image : null;

  useEffect(() => {
    let cancelled = false;
    setImageError(null);
    const next = new Image();
    next.onload = () => {
      if (!cancelled) setFrame({ id: sampleId, image: next });
    };
    next.onerror = () => {
      if (!cancelled) setImageError('图片没有读出来');
    };
    next.src = brandImageUrl(sampleId);
    return () => {
      cancelled = true;
    };
  }, [sampleId]);

  const paint = useMemo(() => {
    return () => {
      const canvas = canvasRef.current;
      if (!canvas || !image) return;
      canvas.width = image.naturalWidth;
      canvas.height = image.naturalHeight;
      const ctx = canvas.getContext('2d');
      if (!ctx) return;
      ctx.drawImage(image, 0, 0);
      const width = canvas.width;
      const height = canvas.height;
      proposals.forEach((proposal) => {
        const color = MODEL_COLOR[proposal.model] || '#8a8175';
        const scale = screenScale(canvas);
        proposal.items.forEach((item) => {
          if (!item.box || item.box.length !== 4) return;
          const [x1, y1, x2, y2] = item.box;
          ctx.save();
          ctx.setLineDash([4 * scale, 3 * scale]);
          strokeBox(ctx, color, x1 * width, y1 * height, (x2 - x1) * width, (y2 - y1) * height, scale, scale);
          ctx.restore();
        });
      });
      items.forEach((item, index) => {
        if (!hasBox(item)) return;
        const color = COLORS[index % COLORS.length];
        const active = selectedId === item.id;
        const [x1, y1, x2, y2] = item.box;
        const scale = screenScale(canvas);
        strokeBox(ctx, color, x1 * width, y1 * height, (x2 - x1) * width, (y2 - y1) * height, (active ? 1.35 : 1) * scale, scale);
        const font = Math.max(11, 12 * scale);
        ctx.font = `600 ${font}px sans-serif`;
        ctx.fillStyle = color;
        const labelY = y1 * height >= font + 4 ? y1 * height - font - 2 : y2 * height + 4;
        ctx.fillText(item.cue, x1 * width, labelY);
        if (active && !drawMode) {
          const grip = 4 * scale;
          for (const [, hx, hy] of handles(item.box)) {
            ctx.fillStyle = '#fff';
            ctx.lineWidth = 1.15 * scale;
            ctx.strokeStyle = color;
            ctx.fillRect(hx * width - grip / 2, hy * height - grip / 2, grip, grip);
            ctx.strokeRect(hx * width - grip / 2, hy * height - grip / 2, grip, grip);
          }
        }
      });
      if (draftBox) {
        const [x1, y1, x2, y2] = draftBox;
        const scale = screenScale(canvas);
        ctx.save();
        ctx.setLineDash([6 * scale, 4 * scale]);
        strokeBox(ctx, DRAFT_COLOR, x1 * width, y1 * height, (x2 - x1) * width, (y2 - y1) * height, 1.25 * scale, scale);
        ctx.restore();
      }
    };
  }, [image, items, proposals, selectedId, draftBox, drawMode]);

  useEffect(() => {
    paint();
  }, [paint]);

  const point = (event: React.PointerEvent) => {
    const canvas = canvasRef.current!;
    const rect = canvas.getBoundingClientRect();
    return {
      x: (event.clientX - rect.left) / rect.width,
      y: (event.clientY - rect.top) / rect.height,
    };
  };

  return (
    <div className="w-full max-w-3xl">
      {!image && !imageError && <div className="text-sm text-[var(--muted)] mb-2">正在读取图片…</div>}
      {imageError && <div className="text-sm text-[var(--red)] mb-2">{imageError}</div>}
      <canvas
        ref={canvasRef}
        className="w-full max-w-3xl h-auto bg-[#efe6d8] rounded-[var(--radius)] touch-none"
        style={{ cursor: drawMode ? 'crosshair' : undefined }}
        onPointerDown={(event) => {
          const canvas = canvasRef.current;
          if (!canvas) return;
          canvas.setPointerCapture(event.pointerId);
          const at = point(event);
          if (drawModeRef.current) {
            drawRef.current = { x: at.x, y: at.y, sx: event.clientX, sy: event.clientY };
            return;
          }
          const hit = hitTest(itemsRef.current, at, canvas, selectedRef.current);
          if (!hit) return;
          onSelect(hit.item.id);
          armRef.current = {
            kind: hit.edge ? 'resize' : 'move',
            itemId: hit.item.id,
            edge: hit.edge,
            dx: at.x - hit.item.box[0],
            dy: at.y - hit.item.box[1],
            origin: [...hit.item.box],
            sx: event.clientX,
            sy: event.clientY,
          };
        }}
        onPointerMove={(event) => {
          const canvas = canvasRef.current;
          const drawing = drawRef.current;
          if (drawing && canvas && image) {
            const at = point(event);
            const box = [Math.min(drawing.x, at.x), Math.min(drawing.y, at.y), Math.max(drawing.x, at.x), Math.max(drawing.y, at.y)];
            paint();
            const ctx = canvas.getContext('2d');
            if (ctx) {
              const scale = screenScale(canvas);
              ctx.save();
              ctx.setLineDash([6 * scale, 4 * scale]);
              strokeBox(ctx, DRAFT_COLOR, box[0] * canvas.width, box[1] * canvas.height, (box[2] - box[0]) * canvas.width, (box[3] - box[1]) * canvas.height, 1.25 * scale, scale);
              ctx.restore();
            }
            return;
          }
          const arm = armRef.current;
          if (arm && !dragRef.current) {
            if (Math.hypot(event.clientX - arm.sx, event.clientY - arm.sy) < 6) return;
            dragRef.current = arm.kind === 'resize'
              ? { kind: 'resize', itemId: arm.itemId, edge: arm.edge, origin: arm.origin }
              : { kind: 'move', itemId: arm.itemId, dx: arm.dx, dy: arm.dy, origin: arm.origin };
          }
          const drag = dragRef.current;
          if (!drag) {
            if (!canvas) return;
            const hit = hitTest(itemsRef.current, point(event), canvas, selectedRef.current);
            canvas.style.cursor = hit
              ? cursorFor(hit.edge, hit.item.id === selectedRef.current)
              : 'default';
            return;
          }
          onPreview(drag.itemId, movedBox(drag, point(event)));
        }}
        onPointerUp={(event) => {
          const drawing = drawRef.current;
          if (drawing) {
            drawRef.current = null;
            const at = point(event);
            const box = [Math.min(drawing.x, at.x), Math.min(drawing.y, at.y), Math.max(drawing.x, at.x), Math.max(drawing.y, at.y)].map(round);
            const moved = Math.hypot(event.clientX - drawing.sx, event.clientY - drawing.sy);
            if (moved >= 6 && box[2] - box[0] >= 0.01 && box[3] - box[1] >= 0.01) onDraw(box);
            return;
          }
          const drag = dragRef.current;
          dragRef.current = null;
          armRef.current = null;
          if (!drag) return;
          const box = movedBox(drag, point(event));
          const changed = drag.origin.some((value, corner) => Math.abs(value - box[corner]) >= 0.0005);
          if (changed) onCommit(drag.itemId, box);
        }}
      />
    </div>
  );
}

function strokeBox(ctx: CanvasRenderingContext2D, color: string, x: number, y: number, w: number, h: number, width: number, scale: number) {
  ctx.lineJoin = 'miter';
  ctx.strokeStyle = 'rgba(0,0,0,0.88)';
  ctx.lineWidth = width + 0.45 * scale;
  ctx.strokeRect(x, y, w, h);
  ctx.strokeStyle = color;
  ctx.lineWidth = width;
  ctx.strokeRect(x, y, w, h);
}

function screenScale(canvas: HTMLCanvasElement) {
  const rect = canvas.getBoundingClientRect();
  return rect.width > 0 ? canvas.width / rect.width : 1;
}

function cursorFor(edge: string, selected: boolean) {
  if (!selected) return 'default';
  if (!edge) return 'grab';
  if (edge === 'n' || edge === 's') return 'ns-resize';
  if (edge === 'e' || edge === 'w') return 'ew-resize';
  if (edge === 'nw' || edge === 'se') return 'nwse-resize';
  if (edge === 'ne' || edge === 'sw') return 'nesw-resize';
  return 'default';
}

function handles(box: number[]) {
  const [x1, y1, x2, y2] = box;
  const mx = (x1 + x2) / 2;
  const my = (y1 + y2) / 2;
  return [
    ['nw', x1, y1], ['n', mx, y1], ['ne', x2, y1], ['w', x1, my],
    ['e', x2, my], ['sw', x1, y2], ['s', mx, y2], ['se', x2, y2],
  ] as const;
}

function hitTest(items: BrandItem[], at: { x: number; y: number }, canvas: HTMLCanvasElement, selected: number | null) {
  const rect = canvas.getBoundingClientRect();
  if (selected != null) {
    const item = items.find((entry) => entry.id === selected);
    if (item && hasBox(item)) {
      const [x1, y1, x2, y2] = item.box;
      const handleX = 5 / Math.max(rect.width, 1);
      const handleY = 5 / Math.max(rect.height, 1);
      const spanX = Math.max(x2 - x1, 0.0001);
      const spanY = Math.max(y2 - y1, 0.0001);
      const rx = (at.x - x1) / spanX;
      const ry = (at.y - y1) / spanY;
      const nearBorder = rx < 0.34 || rx > 0.66 || ry < 0.34 || ry > 0.66;
      if (nearBorder) {
        for (const [edge, x, y] of handles(item.box)) {
          if (Math.abs(at.x - x) <= handleX && Math.abs(at.y - y) <= handleY) {
            return { item, edge };
          }
        }
      }
    }
  }
  let best: { item: BrandItem; edge: string; area: number } | null = null;
  for (const item of items) {
    if (!hasBox(item)) continue;
    const [x1, y1, x2, y2] = item.box;
    if (at.x < x1 || at.x > x2 || at.y < y1 || at.y > y2) continue;
    const area = (x2 - x1) * (y2 - y1);
    if (!best || area < best.area) best = { item, edge: '', area };
  }
  return best;
}

function movedBox(drag: Drag, at: { x: number; y: number }) {
  const box = [...drag.origin];
  if (drag.kind === 'move') {
    const width = box[2] - box[0];
    const height = box[3] - box[1];
    const x1 = clamp(at.x - drag.dx, 0, 1 - width);
    const y1 = clamp(at.y - drag.dy, 0, 1 - height);
    return [round(x1), round(y1), round(x1 + width), round(y1 + height)];
  }
  if (drag.edge.includes('w')) box[0] = clamp(at.x, 0, box[2] - 0.01);
  if (drag.edge.includes('e')) box[2] = clamp(at.x, box[0] + 0.01, 1);
  if (drag.edge.includes('n')) box[1] = clamp(at.y, 0, box[3] - 0.01);
  if (drag.edge.includes('s')) box[3] = clamp(at.y, box[1] + 0.01, 1);
  return box.map(round);
}

function clamp(value: number, min: number, max: number) {
  return Math.min(max, Math.max(min, value));
}

function round(value: number) {
  return Math.round(value * 10000) / 10000;
}
